import { BlotterDensity, usePersistedBlotterTable } from '@talos/kyoko';
import { usePredictionMarketBlotterColumns } from '../predictionMarketBlotter';
import { useOrderColumns } from './Columns';
import { toOrdersServerFilter } from './serverFilter';
import { useCustomerEnrichedOrdersObservable } from './useCustomerEnrichedOrdersObservable';
import { useFilteredOrdersBlotterObs } from './useFilteredOrdersBlotterObs';
import { useOrderFilter, type BlotterTableOrderFilter, type OrderAdditionalFilterState } from './useOrderFilter';

interface UseFilteredOrdersBlotterParams {
  blotterID: string;
  tabLabel?: string;
  defaultFilter?: BlotterTableOrderFilter;
  defaultColumns?: Parameters<typeof useOrderColumns>[0]['defaultColumns'];
  suppressCustomerColumns?: boolean;
  persistFilter?: boolean;
  permanentFilters?: Parameters<typeof useOrderFilter>[0]['permanentFilters'];
  additionalFilterState?: OrderAdditionalFilterState;
  alwaysShowDDHOrders?: boolean;
  includeStatusFilter?: boolean;
  includeIDFilter?: boolean;
}

export function useFilteredOrdersBlotter({
  blotterID,
  tabLabel,
  defaultFilter,
  defaultColumns,
  suppressCustomerColumns,
  persistFilter,
  permanentFilters,
  additionalFilterState,
  alwaysShowDDHOrders,
  includeStatusFilter = true,
  includeIDFilter = true,
}: UseFilteredOrdersBlotterParams) {
  const predictionColumns = usePredictionMarketBlotterColumns();
  const columns = useOrderColumns({ additionalColumns: predictionColumns, defaultColumns, suppressCustomerColumns });

  const persisted = usePersistedBlotterTable(blotterID, { filter: defaultFilter }, { persistFilter });

  const filteredOrders = useOrderFilter({
    initialFilter: persisted.initialState?.filter,
    permanentFilters,
    saveFilter: persisted.onFilterChanged,
    includeStatusFilter,
    includeIDFilter,
    additionalFilterState,
    alwaysShowDDHOrders,
  });

  const serverFilter = toOrdersServerFilter(filteredOrders.filter);
  const { dataObservable, limitReachedState, raisePaginationLimits, errorObservable } = useFilteredOrdersBlotterObs({
    serverFilter,
  });
  const pipedSubscription = useCustomerEnrichedOrdersObservable({ dataObservable, filter: filteredOrders.filter });

  return {
    density: BlotterDensity.Compact,
    rowID: 'OrderID' as const,
    columns,
    persisted,
    filteredOrders,
    dataObservable: pipedSubscription,
    errorObservable,
    limitReachedState,
    raisePaginationLimits,
    exportOptions: { name: 'Orders' as const, tabLabel },
  };
}
